import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CustomButton } from '../Button/Button';
import styles from './ReplyComment.module.scss';
import { Icon } from '../Icon';
import EmojiPicker from 'emoji-picker-react';
import useOutsideClick from '../../hooks/useOutsideClick';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { secondsToTimeString } from '../../utils/TimeUtil';
import { useVideoProgressContext } from '../../context/VideoProgressContext';

const ReplyComment = ({
  user,
  currentUserName,
  setCurrentUserName,
  comment,
  parentAuthor,
  handleCancel,
  onSubmitComment,
  inReplyTo,
  isEdit,
  isReply,
  replyProcessing,
}) => {
  const [newComment, setNewComment] = useState(comment);
  const [nameFieldType, setNameFieldType] = useState('text');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { t } = useTranslation();
  const [isChatIconShown, setIsChatIconShown] = useState(false);

  const [showNameField] = useState(currentUserName === null);

  const actionBtnText = useMemo(() => (isEdit ? t('general.save') : t('general.comment.send')), [isEdit]);


  const { videoProgress } = useVideoProgressContext();

  const emojiRef = useRef();
  const replyRef = useRef();
  const replyNameRef = useRef();
  const containerRef = useRef(null);

  useOutsideClick(emojiRef, () => setShowEmojiPicker(false));

  const handleChangeHeight = e => {
    if (!replyRef.current) {
      return;
    }

    // Reset field height
    replyRef.current.style.height = 'inherit';
    // Get the computed styles for the element
    const computed = window.getComputedStyle(replyRef.current);
    // Calculate the height
    const height =
      parseInt(computed.getPropertyValue('border-top-width'), 10) +
      parseInt(computed.getPropertyValue('padding-top'), 10) +
      replyRef.current.scrollHeight +
      parseInt(computed.getPropertyValue('padding-bottom'), 10) +
      parseInt(computed.getPropertyValue('border-bottom-width'), 10);

    if (height > 96) {
      replyRef.current.style.height = `${height}px`;
    } else {
      replyRef.current.style.height = '96px';
    }
  };

  const handleAddTimestamp = () => {
    setNewComment({
      ...newComment,
      content: `${newComment.content} ${secondsToTimeString(videoProgress.milliseconds / 1000)}`,
    });
  };

  useEffect(() => {
    if (newComment.content === '') {
      replyRef.current.focus();
      replyRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    const content = parentAuthor ? `@${parentAuthor} ` : comment.content;

    if (user && user.userFullName) {
      setNameFieldType('hidden');
      setNewComment({
        ...newComment,
        authorName: user.userFullName,
        content,
      });
    } else if (currentUserName !== null) {
      setNameFieldType('hidden');
      setNewComment({
        ...newComment,
        authorName: currentUserName,
        content,
      });
    } else {
      setNewComment({ ...newComment, content });
    }

    replyRef.current.setSelectionRange(content.length, content.length);

    handleChangeHeight();
  }, []);

  const handleChangeInput = e => {
    if (e.target.name === 'content') {
      setNewComment({ ...newComment, content: e.target.value });
    } else if (e.target.name === 'authorName') {
      setNewComment({ ...newComment, authorName: e.target.value });
      setCurrentUserName(e.target.value);
    }
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const onEmojiClick = emoji => {
    setNewComment({ ...newComment, content: `${newComment.content}${emoji.emoji}` });
  };

  const handleSubmitComment = () => {
    if (inReplyTo !== null) {
      newComment.parent = inReplyTo;
      newComment.parentAuthor = parentAuthor;
    }
    onSubmitComment(newComment);
  };

  const hideChatIcon = () => {
    if ($crisp && !$crisp.is('chat:opened')) {
      setIsChatIconShown(true);
      $crisp.push(['do', 'chat:hide']);
    }
  };

  const handleSaveComment = () => {
    isChatIconShown && $crisp.push(['do', 'chat:show']);
    handleSubmitComment();
  };

  useOutsideClick(containerRef, () => {
    handleSubmitComment();
  });

  return (
    <div
      className={styles.newCommentContainer}
      ref={containerRef}
      style={{ marginBottom: '32px', marginLeft: inReplyTo !== null || isReply ? '48px' : '0px', marginTop: isEdit ? '24px' : '0px' }}
    >
      <div className={styles.newCommandInput} ref={emojiRef}>
        <textarea
          className={styles.commentContent}
          cols="45"
          name="content"
          ref={replyRef}
          value={newComment.content}
          onChange={handleChangeInput}
          placeholder={t('newcomment.add_comment')}
          onInput={handleChangeHeight}
          onFocus={hideChatIcon}
          autoFocus
        />

        {!isMobile && (
          <>
            <Icon src="addEmoji" verticalAlign="middle" className={styles.addEmojiIcon} onClick={() => toggleEmojiPicker()} />
            <CustomButton
              label={t('newcomment.add_time')}
              variant="secondary"
              className={styles.addTimeBtn}
              onClick={handleAddTimestamp}
            />
          </>
        )}
        {!isMobile && showEmojiPicker && (
          <div className={styles.emojiPicker}>
            <EmojiPicker onEmojiClick={onEmojiClick} previewConfig={{ showPreview: false }} />
          </div>
        )}

        {newComment.content.trim() !== '' && (
          <div className={styles.actionButtonsContainer}>
            <CustomButton
              label={replyProcessing ? t('general.saving') : actionBtnText}
              variant="secondary"
              customStyles={!newComment.authorName || !newComment.content ? { opacity: ' 0.3' } : {}}
              disabled={!newComment.authorName || !newComment.content || replyProcessing}
              className={styles.saveBtn}
              onClick={handleSaveComment}
            />
          </div>
        )}
      </div>
      {user === null && showNameField && (
        <input
          placeholder={t('general.your_name')}
          maxLength={130}
          className={styles.replyNameField}
          ref={replyNameRef}
          type={nameFieldType}
          name="authorName"
          value={newComment.authorName}
          onChange={handleChangeInput}
        />
      )}
    </div>
  );
};

export default ReplyComment;
